import { useEffect, useState, useCallback, useMemo } from 'react';
import { Loader2, Play, RotateCw } from 'lucide-react';
import { fieldPartnersApi, type IncentivePayout, type PayoutStatus } from '@/api/field-partners';
import { formatINR, formatDate } from '@/utils/formatters';
import { FieldProgramGate } from '@/components/field-partners/FieldProgramGate';
import type { FieldProgramConfig } from '@/hooks/useFieldProgramConfig';

const STATUS_COLORS: Record<PayoutStatus, string> = {
  QUEUED: 'bg-gray-100 text-gray-700',
  PROCESSING: 'bg-blue-50 text-blue-700',
  SUCCESS: 'bg-green-50 text-green-700',
  FAILED: 'bg-red-50 text-red-700',
};

function PayoutsInner({ config }: { config: FieldProgramConfig | null }) {
  const partnerLabel = config?.partnerTypeLabel || 'Field Partner';

  const [rows, setRows] = useState<IncentivePayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [processing, setProcessing] = useState(false);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fieldPartnersApi.listPayouts(
        filterStatus ? { status: filterStatus } : {},
      );
      setRows(data);
    } catch (err: any) {
      if (err.response?.status === 404) setRows([]);
      else setError(err.response?.data?.error || 'Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((p) => p.partnerName.toLowerCase().includes(q));
  }, [rows, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.total += r.amount;
        if (r.status === 'SUCCESS') acc.success += r.amount;
        if (r.status === 'QUEUED' || r.status === 'PROCESSING') acc.pending += r.amount;
        if (r.status === 'FAILED') acc.failed += r.amount;
        return acc;
      },
      { total: 0, success: 0, pending: 0, failed: 0 },
    );
  }, [rows]);

  const processPending = async () => {
    if (!confirm('Process all pending (QUEUED) payouts?')) return;
    setProcessing(true);
    try {
      await fieldPartnersApi.processPending();
      await fetch();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed');
    } finally {
      setProcessing(false);
    }
  };

  const retry = async (id: string) => {
    try {
      await fieldPartnersApi.retryPayout(id);
      await fetch();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Retry failed');
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Payouts Console</h1>
          <p className="text-sm text-[var(--text-muted)]">
            UPI / bank incentive payouts to {partnerLabel.toLowerCase()}s
          </p>
        </div>
        <button
          onClick={processPending}
          disabled={processing}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-60"
        >
          <Play size={16} />
          {processing ? 'Processing...' : 'Process Pending'}
        </button>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Total', value: formatINR(totals.total), color: 'text-[var(--text-primary)]' },
          { label: 'Success', value: formatINR(totals.success), color: 'text-green-600' },
          { label: 'Pending', value: formatINR(totals.pending), color: 'text-amber-600' },
          { label: 'Failed', value: formatINR(totals.failed), color: 'text-red-600' },
        ].map((s) => (
          <div
            key={s.label}
            className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4"
          >
            <p className="text-xs uppercase text-[var(--text-muted)]">{s.label}</p>
            <p className={`mt-1 text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search by ${partnerLabel.toLowerCase()} name...`}
          className="min-w-[220px] flex-1 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          {Object.keys(STATUS_COLORS).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          </div>
        ) : error ? (
          <div className="py-20 text-center text-sm text-red-600">{error}</div>
        ) : paged.length === 0 ? (
          <div className="py-20 text-center text-sm text-[var(--text-muted)]">No payouts</div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-inset)] text-left text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                  <th className="px-4 py-3">{partnerLabel}</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Destination</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Triggered</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-primary)]">
                {paged.map((p) => (
                  <tr key={p.id} className="hover:bg-[var(--bg-surface-hover)]">
                    <td className="px-4 py-3 font-medium text-[var(--text-primary)]">
                      {p.partnerName}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-[var(--text-primary)]">
                      {formatINR(p.amount)}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[var(--text-secondary)]">
                      {p.upiVpa || p.bankAccount || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[p.status]}`}
                      >
                        {p.status}
                      </span>
                      {p.failureReason && (
                        <p className="mt-0.5 text-[10px] text-red-600">{p.failureReason}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">
                      {formatDate(p.triggeredAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {p.status === 'FAILED' && (
                        <button
                          onClick={() => retry(p.id)}
                          className="flex items-center justify-end gap-1 text-sm text-[var(--ink-0)] hover:underline"
                        >
                          <RotateCw size={12} /> Retry
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pageCount > 1 && (
              <div className="flex items-center justify-between border-t border-[var(--border-primary)] px-4 py-2 text-xs text-[var(--text-muted)]">
                <span>
                  Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of{' '}
                  {filtered.length}
                </span>
                <div className="flex gap-2">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded border border-[var(--border-primary)] px-2 py-1 disabled:opacity-40"
                  >
                    Prev
                  </button>
                  <span>
                    {page} / {pageCount}
                  </span>
                  <button
                    disabled={page === pageCount}
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    className="rounded border border-[var(--border-primary)] px-2 py-1 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function PayoutsConsolePage() {
  return <FieldProgramGate>{(config) => <PayoutsInner config={config} />}</FieldProgramGate>;
}

export default PayoutsConsolePage;
